/**
 * PLAT-10915 — run one AI text generation and land its result.
 *
 * The orchestration between the repository call and the editor: call the
 * prompt, write the preferred option to the element, record the provenance.
 * Pure per the services contract — no MobX, no React, and the repository is
 * injected so the creation path has no hard dependency on a network call.
 *
 * Two decisions are load-bearing here:
 *
 *  - **The write-back is `modifyProperty`, nothing more** (E2 / DS4). The
 *    generated text is written through the same path a creator typing into the
 *    field takes, which buys the renderer repaint, undo, Save/Update Template
 *    and the composition-translation flow with no new code — and leaves the
 *    text hand-editable afterwards, because it is just the text property.
 *  - **The element is re-resolved by id at write time** (DS3). A generation is
 *    fired and awaited by nothing; by the time it lands the graphic may have
 *    been deleted or the surface unmounted. `setCompositionFieldValue` looks
 *    the element up on the timeline and returns `false` when it is gone, so a
 *    late response no-ops instead of writing into a stale reference.
 */
import { err, ok } from '@src/services/common/Result';
import { fingerprintAITextBaseText } from '@src/utility/aiTextFingerprint';
import { extractErrorInfo } from '@src/utility/error';
import {
  getAiTextProvenance,
  upsertAiTextProvenance,
} from '@src/utility/extraElementData';
import { LogManager } from '@src/utility/Logger';
import { setCompositionFieldValue } from '@src/videoTranscriptionProcessor/translation/setCompositionFieldValue';

import type { AITextBaseTextScope } from '@src/components/sidepanel/CompositionSettings/types';
import type {
  AITextGenerationRequest,
  generateAIText,
} from '@src/repositories/AITextGenerationRepository';
import type { Result } from '@src/services/common/Result';
import type { EditorStoreLike } from '@src/utility/compositionSettings';
import type { VideoLike } from '@src/utility/extraElementData';

const logger = LogManager.getLogger('service:aiTextGeneration');

/** Where a generation writes: one control's leaf on one composition placement. */
export interface AITextGenerationTarget {
  /** Top-level composition placement (or standalone overlay) element id. */
  elementId: string;
  /** Control id — provenance is keyed by it (E1/E5). */
  controlId: string;
  /** DatoCMS `ai_prompt` record id from the control's config. */
  promptId: string;
  /** Leaf property holding the text (`'text'`). */
  property: string;
  /** Path from the placement to the leaf (`control.elementPaths[0]`). */
  elementPath: ReadonlyArray<number>;
}

export interface GenerateCompositionAITextParams {
  target: AITextGenerationTarget;
  /** Which scope produced `baseText` — recorded so PLAT-10918 compares like for like. */
  baseTextScope: AITextBaseTextScope;
  /** The resolved words, from `resolveAITextBaseText`. */
  baseText: string;
  /** The creator's steer. PLAT-10917 is what sends a non-empty value. */
  instructions?: string;
  /** Context BY REFERENCE — the backend resolves the Brand Kit from it (E3). */
  context: { storyId?: string; showcaseId?: string; clipId?: string };
  editorStore: EditorStoreLike;
  /** Live current video — called at write time, never cached (service contract). */
  getCurrentVideo: () => VideoLike;
  /** Immediate repaint; the command's `markDirty` sync is debounced (~300ms). */
  refreshSource?: () => void;
  /** Injected repository call. */
  generate: typeof generateAIText;
  /**
   * PLAT-10917 DS5 — asked once, after the model responds and before anything
   * is written: has a newer run for this same control started meanwhile? A
   * superseded run writes NOTHING rather than landing its older text and its
   * older options on top of the newer ones.
   *
   * The disabled Generate button is not sufficient on its own: the two
   * clip-preview surfaces hold separate `useAITextGeneration` instances, so an
   * insert-time run in flight on the transcript side leaves Generate live on
   * the canvas side. Omit and no run is ever considered superseded.
   */
  isSuperseded?: () => boolean;
  /** Overridable clock, so the provenance timestamp is assertable in tests. */
  now?: () => string;
}

export interface GeneratedAIText {
  /** Every option the model returned, preferred first (E9). */
  options: string[];
  /** The option written to the element. */
  text: string;
}

export async function generateCompositionAIText(
  params: GenerateCompositionAITextParams,
): Promise<Result<GeneratedAIText>> {
  const {
    target,
    baseTextScope,
    baseText,
    instructions,
    context,
    editorStore,
    getCurrentVideo,
    refreshSource,
    generate,
    isSuperseded,
    now = () => new Date().toISOString(),
  } = params;

  if (!target.elementId || !target.controlId) {
    return err('VALIDATION', 'AI text target is missing an element or control');
  }
  if (!target.promptId) {
    return err('VALIDATION', 'AI text control has no prompt configured');
  }
  if (baseText.trim() === '') {
    return err('PRECONDITION', 'This graphic has no transcript text to read');
  }

  const request: AITextGenerationRequest = {
    promptId: target.promptId,
    storyId: context.storyId,
    showcaseId: context.showcaseId,
    clipId: context.clipId,
    baseText,
    baseTextScope,
    ...(instructions ? { instructions } : {}),
  };

  let options: string[];
  try {
    options = await generate(request);
  } catch (error) {
    logger.error('AI text generation failed', {
      elementId: target.elementId,
      controlId: target.controlId,
      promptId: target.promptId,
      ...extractErrorInfo(error),
    });
    return err(
      'EXTERNAL',
      extractErrorInfo(error).errorMessage || 'AI text generation failed',
      error,
    );
  }

  // PLAT-10917 DS5 — a newer run for this control started while the model was
  // thinking. Bail before the write, not after: the whole point is that the
  // LAST run the creator asked for is the one on the canvas, and this one's
  // options would otherwise replace the newer set in the blob too.
  if (isSuperseded?.()) {
    logger.debug('Discarding a superseded AI text generation', {
      elementId: target.elementId,
      controlId: target.controlId,
    });
    return err('PRECONDITION', 'A newer generation replaced this one');
  }

  // `generate` is injected, so the repository's own "no usable option throws"
  // guarantee isn't one this function can assume — without this an empty list
  // would write `undefined` into the element.
  const text = options[0];
  if (typeof text !== 'string' || text === '') {
    logger.error('AI text generation returned no usable option', {
      elementId: target.elementId,
      controlId: target.controlId,
      promptId: target.promptId,
    });
    return err('EXTERNAL', 'The model returned no usable text');
  }

  const written = setCompositionFieldValue({
    editorStore,
    targetElementId: target.elementId,
    elementPath: target.elementPath,
    property: target.property,
    newValue: text,
  });
  if (!written) {
    // The graphic was deleted (or its path no longer resolves) while the model
    // was thinking. Nothing to write into and nothing to record.
    logger.warn('AI text landed on a graphic that is no longer there', {
      elementId: target.elementId,
      controlId: target.controlId,
    });
    return err('NOT_FOUND', 'The graphic is no longer on the timeline');
  }

  refreshSource?.();

  // All five options now, even though the picker is PLAT-10916 — writing four
  // of them later would mean a second write path for the same data (DS6).
  upsertAiTextProvenance(
    getCurrentVideo(),
    target.elementId,
    target.controlId,
    {
      baseTextScope,
      sourceFingerprint: fingerprintAITextBaseText(baseText),
      ...(instructions ? { instructions } : {}),
      generatedAt: now(),
      options,
      selectedOption: 0,
    },
  );

  return ok({ options, text });
}

/**
 * PLAT-10916 — apply one of the already-generated options.
 *
 * Deliberately synchronous and network-free (DS3): the options are already in
 * the provenance blob, so switching costs a `modifyProperty` and an index
 * write. The blob is the source of truth for the LIST — `options` is never
 * rewritten here, only `selectedOption` — which is why a re-run is the one
 * thing that can change what the picker offers (E9).
 *
 * The element is re-resolved by id at write time for the same reason a
 * generation is: the graphic can be gone by the time the click lands.
 */
export interface SelectCompositionAITextOptionParams {
  /** Where to write. No `promptId` — nothing is generated here. */
  target: Omit<AITextGenerationTarget, 'promptId'>;
  /** Index into the blob's `options`. */
  optionIndex: number;
  editorStore: EditorStoreLike;
  getCurrentVideo: () => VideoLike;
  refreshSource?: () => void;
}

export function selectCompositionAITextOption(
  params: SelectCompositionAITextOptionParams,
): Result<string> {
  const { target, optionIndex, editorStore, getCurrentVideo, refreshSource } =
    params;

  const video = getCurrentVideo();
  const provenance = getAiTextProvenance(
    video,
    target.elementId,
    target.controlId,
  );
  if (!provenance) {
    logger.warn(
      'AI text option picked on a control with no generation record',
      {
        elementId: target.elementId,
        controlId: target.controlId,
        optionIndex,
      },
    );
    return err('NOT_FOUND', 'This control has no generated options');
  }

  // The blob is persisted JSON — an index out of range, or an entry whose
  // options array holds something other than strings, is a runtime
  // possibility, not a type error.
  const text = provenance.options[optionIndex];
  if (typeof text !== 'string' || text === '') {
    logger.warn('AI text option is not usable', {
      elementId: target.elementId,
      controlId: target.controlId,
      optionIndex,
      optionCount: provenance.options.length,
    });
    return err('VALIDATION', 'That option is no longer available');
  }

  const written = setCompositionFieldValue({
    editorStore,
    targetElementId: target.elementId,
    elementPath: target.elementPath,
    property: target.property,
    newValue: text,
  });
  if (!written) {
    logger.warn('AI text option applied to a graphic that is no longer there', {
      elementId: target.elementId,
      controlId: target.controlId,
    });
    return err('NOT_FOUND', 'The graphic is no longer on the timeline');
  }

  // Record the pick BEFORE repainting. The repaint reads the element, not the
  // blob, so the order doesn't matter today — but "model fully updated, then
  // tell the world" is the order that stays correct if it ever does.
  upsertAiTextProvenance(video, target.elementId, target.controlId, {
    ...provenance,
    selectedOption: optionIndex,
  });

  refreshSource?.();

  return ok(text);
}
